import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Row, Col, Table, Button } from 'react-bootstrap';
import { X, CalendarDays } from 'lucide-react';
import { User } from '../../services/dashboardService';
import { WalletBalance } from '../../services/walletService';
import { cn } from '../../utils/cn';

interface TransactionFees {
  superAgentPercent: number;
  agentPercent: number;
  superAgent: number;
  agent: number;
  netPayout: number;
}

interface Payout {
  id: number;
  transactionId: number;
  type: 'Admin' | 'Agent' | 'SuperAgent';
  toWallet: string;
  amount: string;
  createtAt: string;
  createtBy: string;
  txHash: string;
  status: string;
}

interface CheckoutSession {
  id: number;
  transactionId: number;
  paymentMethod: string;
  url: string;
  status: string;
  createdAt: string;
  expireAt: string;
}

export interface PaymentReviewTransaction {
  id: number;
  userId: number;
  provider: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoCurrency: string;
  cryptoAmount: number;
  userWallet: string;
  uniqueWalletAddress: string;
  status: 'Issued' | 'Completed' | 'Pending' | 'Failed' | 'PayoutCompleted';
  timestamp: string;
  txHash?: string;
  fees: TransactionFees;
  payouts?: Payout[];
  checkoutSessions?: CheckoutSession[];
}

type TimelineDot = 'blue' | 'cyan' | 'gray' | 'green' | 'orange';

const dotClass: Record<TimelineDot, string> = {
  blue: 'bg-[#2297D1]',
  cyan: 'bg-cyan-500',
  gray: 'bg-slate-400',
  green: 'bg-emerald-500',
  orange: 'bg-orange-500',
};

export interface PaymentReviewModalProps {
  show: boolean;
  onHide: () => void;
  transaction: PaymentReviewTransaction | null;
  userRole: 'Admin' | 'SuperAgent' | 'Agent' | null;
  walletBalances: { [address: string]: WalletBalance };
  loadingBalances: boolean;
  manualSettling: boolean;
  onRefresh: () => void;
  onManualSettle: (
    transactionId: number,
    payload: { cryptoAmount: number; cryptoCurrency: string; fundingTxHash?: string; note?: string }
  ) => Promise<void>;
  getUsernameById: (uid: number) => string;
  getUserById: (uid: number) => User | undefined;
  getSuperAgentForAgent: (agentId: number) => User | undefined;
  expandedSections: { generatedLinks: boolean; wallets: boolean };
  toggleSection: (section: 'generatedLinks' | 'wallets') => void;
}

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString();

const REVIEW_STATE_STORAGE_KEY = 'zkGate.paymentReviewState';

type StoredReviewState = {
  reviewPhase: 'open' | 'inopen' | 'resolved';
  reviewOwner: string;
  dueDate: string;
  reminderOn: boolean;
  reviewNote: string;
  teamNote: string;
};

function loadStoredReviewState(txId: number): StoredReviewState | null {
  try {
    const raw = localStorage.getItem(REVIEW_STATE_STORAGE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw) as Record<string, StoredReviewState>;
    return all[String(txId)] ?? null;
  } catch {
    return null;
  }
}

function saveStoredReviewState(txId: number, state: StoredReviewState): void {
  try {
    const raw = localStorage.getItem(REVIEW_STATE_STORAGE_KEY);
    const all: Record<string, StoredReviewState> = raw ? JSON.parse(raw) : {};
    all[String(txId)] = state;
    localStorage.setItem(REVIEW_STATE_STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage optional */
  }
}

function headerStatusLabel(status: PaymentReviewTransaction['status']): string {
  switch (status) {
    case 'Completed':
    case 'PayoutCompleted':
      return 'RECEIVED (SETTLED)';
    case 'Failed':
      return 'UNDERPAID (SETTLED)';
    case 'Pending':
      return 'PENDING REVIEW';
    default:
      return 'ISSUED';
  }
}

const PaymentReviewModal: React.FC<PaymentReviewModalProps> = ({
  show,
  onHide,
  transaction: tx,
  userRole,
  walletBalances,
  loadingBalances,
  manualSettling,
  onRefresh,
  onManualSettle,
  getUsernameById,
  getUserById,
  getSuperAgentForAgent,
  expandedSections,
  toggleSection,
}) => {
  const [reviewOwner, setReviewOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reminderOn, setReminderOn] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [teamNote, setTeamNote] = useState('');
  const [reviewPhase, setReviewPhase] = useState<'open' | 'inopen' | 'resolved'>('open');
  const [manualAmount, setManualAmount] = useState('');
  const [manualCurrency, setManualCurrency] = useState('USDC');
  const [fundingTxHash, setFundingTxHash] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);

  useEffect(() => {
    if (!tx?.id) return;
    const stored = loadStoredReviewState(tx.id);
    if (stored) {
      setReviewOwner(stored.reviewOwner);
      setDueDate(stored.dueDate);
      setReminderOn(stored.reminderOn);
      setReviewNote(stored.reviewNote);
      setTeamNote(stored.teamNote);
      setReviewPhase(stored.reviewPhase);
    } else {
      setReviewOwner('');
      setDueDate('');
      setReminderOn(false);
      setReviewNote('');
      setTeamNote('');
      setReviewPhase(tx.status === 'Completed' || tx.status === 'PayoutCompleted' ? 'resolved' : 'open');
    }
    setManualAmount(tx.cryptoAmount ? String(tx.cryptoAmount) : '');
    setManualCurrency((tx.cryptoCurrency || 'USDC').toUpperCase());
    setFundingTxHash(tx.txHash || '');
    setManualNote('');
    setManualError(null);
  }, [tx?.id, tx?.status]);

  useEffect(() => {
    if (!tx?.id) return;
    saveStoredReviewState(tx.id, {
      reviewPhase,
      reviewOwner,
      dueDate,
      reminderOn,
      reviewNote,
      teamNote,
    });
  }, [tx?.id, reviewPhase, reviewOwner, dueDate, reminderOn, reviewNote, teamNote]);

  const timeline = useMemo(() => {
    if (!tx) return [];
    const dots: TimelineDot[] = ['blue', 'cyan', 'gray', 'green', 'orange'];
    const items: {
      title: string;
      at: string;
      description: string;
      source: string;
      dot: TimelineDot;
    }[] = [
      {
        title: 'Payment request created',
        at: formatDateTime(tx.timestamp),
        description: 'The payment was created and assigned a unique deposit address.',
        source: 'transaction-service',
        dot: dots[0],
      },
    ];
    if (tx.checkoutSessions?.length) {
      tx.checkoutSessions.forEach((s, i) => {
        items.push({
          title: `Checkout link ${i + 1} generated`,
          at: formatDateTime(s.createdAt),
          description: `Payment method ${s.paymentMethod}. Session status: ${s.status}.`,
          source: 'checkout-service',
          dot: dots[1 % dots.length],
        });
      });
    }
    if (tx.status === 'Completed' || tx.status === 'PayoutCompleted') {
      items.push({
        title: 'Crypto received',
        at: formatDateTime(tx.timestamp),
        description: `Received ${tx.cryptoAmount} ${tx.cryptoCurrency} on the unique wallet.`,
        source: 'chain-monitor',
        dot: 'green',
      });
    }
    if (tx.payouts?.length) {
      items.push({
        title: 'Settlement claimed',
        at: formatDateTime(tx.payouts[0].createtAt),
        description: 'Payout records were created for this payment.',
        source: 'settlement-service',
        dot: 'orange',
      });
    }
    if (tx.status === 'Failed' || tx.status === 'Pending') {
      items.push({
        title: 'Review required',
        at: formatDateTime(tx.timestamp),
        description: 'This payment needs operator follow-up or reconciliation.',
        source: 'ops-queue',
        dot: 'orange',
      });
    }
    return items;
  }, [tx]);

  if (!tx) return null;

  const accountName = getUsernameById(tx.userId);
  const canManuallySettle = (userRole === 'Admin' || userRole === 'SuperAgent') && (!tx.payouts || tx.payouts.length === 0);
  const receivedLine =
    tx.status === 'Completed' || tx.status === 'PayoutCompleted'
      ? `${tx.cryptoAmount} ${tx.cryptoCurrency}`
      : tx.cryptoAmount
        ? `${tx.cryptoAmount} ${tx.cryptoCurrency}`
        : '—';
  const distributedLine =
    (tx.status === 'Completed' || tx.status === 'PayoutCompleted') && tx.payouts?.length
      ? `${tx.payouts.reduce((acc, p) => acc + parseFloat(p.amount || '0'), 0) || tx.cryptoAmount} ${tx.cryptoCurrency}`
      : tx.status === 'Completed' || tx.status === 'PayoutCompleted'
        ? `${tx.cryptoAmount} ${tx.cryptoCurrency}`
        : '—';

  const checkoutCount = tx.checkoutSessions?.length ?? 0;

  const reviewStateText =
    reviewPhase === 'resolved' ? 'Resolved' : reviewPhase === 'inopen' ? 'In Open' : 'Open';

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* clipboard optional */
    }
  };

  const submitManualSettlement = async () => {
    const amount = Number(manualAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setManualError('Enter a valid crypto amount before settling this payment.');
      return;
    }

    try {
      setManualError(null);
      await onManualSettle(tx.id, {
        cryptoAmount: amount,
        cryptoCurrency: manualCurrency.trim().toUpperCase() || 'USDC',
        fundingTxHash: fundingTxHash.trim() || undefined,
        note: manualNote.trim() || undefined,
      });
    } catch (error) {
      setManualError(error instanceof Error ? error.message : 'Manual settlement failed.');
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="payment-review-modal"
      contentClassName="blurLayerInteractive-modal border border-slate-200/90 shadow-2xl rounded-3xl"
    >
      <Modal.Body className="relative p-2">
      <div className="flex flex-wrap items-start justify-between lg:p-5 p-2 z-50 gap-4">
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Payment Review</p>
                <h2 className="mb-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Payment #{tx.id}</h2>
                <p className="mb-0 max-w-xl text-sm text-slate-500">
                  Review the payment journey, team follow-up, and payout details.
                </p>
              </div>
              <div className="flex flex-shrink-0 flex-wrap items-center gap-2 sm:gap-3 z-50">
                <span className="inline-flex rounded-lg bg-[#e0edf5] px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#1e3a5f]">
                  {headerStatusLabel(tx.status)}
                </span>
                <button
                  type="button"
                  onClick={onRefresh}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={onHide}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>
          </div>

        <div className="space-y-6 px-6 py-6 ">
      
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl   bg-white p-4 shadow-sm border-l-4 border-[#2297D1]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#2297D1]">Requested</p>
                <p className="mt-2 text-2xl font-bold text-slate-900">{formatAmount(tx.fiatAmount, tx.fiatCurrency)}</p>
                <p className="mt-1.5 text-xs leading-snug text-slate-500">Quoted in USDC when the payer chooses crypto (Polygon).</p>
              </div>
              <div className="rounded-xl   bg-white p-4 shadow-sm border-l-4 border-l-emerald-600">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">Payment route</p>
                <p className="mt-2 text-2xl font-bold text-slate-900">{tx.provider || 'DirectCrypto'}</p>
                <p className="mt-1.5 text-xs leading-snug text-slate-500">Status updated {formatDateTime(tx.timestamp)}</p>
              </div>
              <div className="rounded-xl   bg-white p-4 shadow-sm border-l-4 border-l-slate-400">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Account</p>
                <p className="mt-2 text-2xl font-bold text-slate-900">{accountName}</p>
                <p className="mt-1.5 text-xs leading-snug text-slate-500">Merchant account</p>
              </div>
              <div className="rounded-xl bg-white p-4 shadow-sm border-l-4 border-l-orange-500">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-orange-600">Review state</p>
                <span className="mt-2 inline-flex rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1 text-sm font-semibold text-orange-900">
                  {reviewPhase === 'resolved' ? 'Resolved' : 'Open'}
                </span>
                <p className="mt-1.5 text-xs leading-snug text-slate-500">
                  {reviewPhase === 'resolved'
                    ? 'This review has been marked resolved.'
                    : 'No review deadline is set for this payment yet.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-2  pb-4 text-xs text-slate-600">
              <p className="mb-0">
                <span className="font-semibold uppercase tracking-wide text-slate-400">Received </span>
                {receivedLine}
              </p>
              <p className="mb-0">
                <span className="font-semibold uppercase tracking-wide text-slate-400">Distributed </span>
                {distributedLine}
              </p>
              <p className="mb-0">
                <span className="font-semibold uppercase tracking-wide text-slate-400">Last updated </span>
                {formatDateTime(tx.timestamp)}
              </p>
            </div>

            <section>
              <h3 className="mb-3 text-base font-bold text-slate-900">Timeline</h3>
              <div className="relative pl-6">
                <div className="absolute bottom-6 left-[7px] top-2 w-px bg-slate-200" aria-hidden />
                <ul className="relative space-y-3">
                  {timeline.map((item, idx) => (
                    <li key={`${item.title}-${idx}`} className="relative">
                      <span
                        className={cn(
                          'absolute -left-[18px] top-4 h-2.5 w-2.5 rounded-full ring-4 ring-[#f8fafc]',
                          dotClass[item.dot]
                        )}
                        aria-hidden
                      />
                      <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <p className="mb-0 font-semibold text-slate-900">{item.title}</p>
                          <p className="mb-0 text-xs text-slate-500">{item.at}</p>
                        </div>
                        <p className="mb-2 mt-2 text-sm text-slate-600">{item.description}</p>
                        <p className="mb-0 text-[11px] text-slate-400">
                          Source: <span className="text-slate-500">{item.source}</span>
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200/80 bg-slate-100/80 p-5 shadow-inner">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="mb-0 text-sm font-semibold text-slate-800">
                  Current review state <span className="text-[#2297D1]">{reviewStateText}</span>
                </p>
                <p className="mb-0 mt-1 text-xs text-slate-500">
                  Saved locally for this payment — use the buttons below to track operator follow-up (not sent to the server).
                </p>
                <div className="flex flex-wrap gap-2">
                  {(['open', 'inopen', 'resolved'] as const).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setReviewPhase(key)}
                      className={cn(
                        'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                        reviewPhase === key
                          ? 'border-[#2297D1] bg-[#2297D1]/10 text-[#2297D1]'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      )}
                    >
                      {key === 'open' ? 'Mark Open' : key === 'inopen' ? 'Mark In Open' : 'Mark Reviewed'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Review owner</label>
                  <select
                    value={reviewOwner}
                    onChange={(e) => setReviewOwner(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                  >
                    <option value="">Unassign</option>
                    <option value="me">Me</option>
                    <option value="team">Team</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Due date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-10 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                    />
                    <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
              </div>

              <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={reminderOn}
                  onChange={(e) => setReminderOn(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#2297D1] focus:ring-[#2297D1]"
                />
                Enable reminder
              </label>

              <div className="mt-4">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Review update note</label>
                <textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  rows={3}
                  className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                  placeholder="Add context for the next reviewer…"
                />
                <button
                  type="button"
                  className="mt-2 rounded-lg border-2 border-[#2297D1] bg-white px-4 py-2 text-sm font-semibold text-[#2297D1] transition-colors hover:bg-[#2297D1]/5"
                >
                  Save review plan
                </button>
              </div>

              <div className="mt-6 border-t border-slate-200/90 pt-5">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Noted</label>
                <textarea
                  value={teamNote}
                  onChange={(e) => setTeamNote(e.target.value)}
                  rows={3}
                  className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                  placeholder="Record what happened for the team…"
                />
                <button
                  type="button"
                  className="mt-2 rounded-lg bg-[#2297D1] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1d81b2]"
                >
                  Save Note
                </button>
              </div>

              <p className="mb-0 mt-4 text-sm text-slate-500">No team notes have been added yet.</p>
            </section>

            <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-900">Funding Details</span>
                <button
                  type="button"
                  onClick={() => toggleSection('wallets')}
                  className="text-sm font-semibold text-[#2297D1] hover:underline"
                >
                  {expandedSections.wallets ? 'Hide' : 'Show'}
                </button>
              </div>
              {expandedSections.wallets && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  {(() => {
                    const user = getUserById(tx.userId);
                    const walletLabel =
                      user?.role === 'Agent' ? 'Agent Wallet' : user?.role === 'SuperAgent' ? 'SuperAgent Wallet' : 'User Wallet';
                    return (
                      <div className="mb-3">
                        <strong className="text-sm text-[#2297D1]">{walletLabel}:</strong>
                        <div className="mt-1 rounded-lg border border-slate-200 bg-white p-2 font-mono text-sm">{tx.userWallet}</div>
                      </div>
                    );
                  })()}
                  {(userRole === 'Admin' || userRole === 'SuperAgent') && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <strong className="text-sm text-[#2297D1]">Deposit Wallet Address:</strong>
                        <button
                          type="button"
                          onClick={() => void handleCopy(tx.uniqueWalletAddress)}
                          className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-[#2297D1] hover:bg-sky-50"
                        >
                          Copy wallet
                        </button>
                      </div>
                      <div className="mt-1 rounded-lg border border-slate-200 bg-white p-2 font-mono text-sm break-all">
                        {tx.uniqueWalletAddress}
                      </div>
                      {walletBalances[tx.uniqueWalletAddress] && (
                        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2 text-sm">
                          <div className="font-semibold text-[#2297D1]">Balance</div>
                          <div>USDC: {walletBalances[tx.uniqueWalletAddress].usdtBalance}</div>
                          <div>MATIC: {walletBalances[tx.uniqueWalletAddress].maticBalance}</div>
                        </div>
                      )}
                      {loadingBalances && <p className="mt-2 text-sm text-slate-500">Loading balance…</p>}
                    </div>
                  )}
                  {userRole === 'Admin' &&
                    (() => {
                      const user = getUserById(tx.userId);
                      if (user?.role === 'Agent') {
                        const superAgent = getSuperAgentForAgent(user.id);
                        return (
                          <div>
                            <strong className="text-sm text-[#2297D1]">SuperAgent Wallet:</strong>
                            <div className="mt-1 rounded-lg border border-slate-200 bg-white p-2 text-sm">
                              {superAgent?.usdtWallet || 'No wallet address configured'}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  {(userRole === 'Admin' || userRole === 'SuperAgent') && (
                    <div className="mt-4">
                      <strong className="text-sm">Allocation Breakdown:</strong>
                      <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                        <Row>
                          <Col md={6}>Company / platform fee: {tx.fees.superAgentPercent + tx.fees.agentPercent}%</Col>
                          <Col md={6}>Merchant retained share: {100 - tx.fees.superAgentPercent - tx.fees.agentPercent}%</Col>
                        </Row>
                      </div>
                    </div>
                  )}
                  {canManuallySettle && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <div className="mb-3">
                        <strong className="text-sm text-amber-900">Manual settlement controls</strong>
                        <p className="mb-0 mt-1 text-xs text-amber-800">
                          Use this after the client sends funds to the deposit wallet. It marks the payment completed and calls the existing payout claim flow.
                        </p>
                      </div>
                      {manualError && (
                        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                          {manualError}
                        </div>
                      )}
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Received amount</label>
                          <input
                            type="number"
                            min="0"
                            step="0.000001"
                            value={manualAmount}
                            onChange={(e) => setManualAmount(e.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                            placeholder="0.5"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Token</label>
                          <input
                            type="text"
                            value={manualCurrency}
                            onChange={(e) => setManualCurrency(e.target.value.toUpperCase())}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                            placeholder="USDC"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Funding transaction hash (optional)</label>
                          <input
                            type="text"
                            value={fundingTxHash}
                            onChange={(e) => setFundingTxHash(e.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                            placeholder="0x..."
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Operator note (optional)</label>
                          <textarea
                            rows={3}
                            value={manualNote}
                            onChange={(e) => setManualNote(e.target.value)}
                            className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"
                            placeholder="Manual Polygon funding confirmed by client."
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void submitManualSettlement()}
                          disabled={manualSettling}
                          className="rounded-lg bg-[#2297D1] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1d81b2] disabled:opacity-50"
                        >
                          {manualSettling ? 'Settling...' : 'Mark complete and claim payout'}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleCopy(tx.uniqueWalletAddress)}
                          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Copy deposit wallet
                        </button>
                      </div>
                    </div>
                  )}
                  {(userRole === 'Admin' || userRole === 'SuperAgent') && tx.payouts && tx.payouts.length > 0 && (
                    <div className="mt-4">
                      <strong className="text-sm">Settlements ({tx.payouts.length}):</strong>
                      <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
                        <Table responsive striped hover className="mb-0 text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th>Date</th>
                              <th>Type</th>
                              <th>Amount (USDC)</th>
                              <th>Transaction</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tx.payouts.map((payout) => {
                              const typeLabel =
                                payout.type === 'Admin'
                                  ? 'Company'
                                  : payout.type === 'Agent'
                                    ? 'Merchant'
                                    : payout.type === 'SuperAgent'
                                      ? 'Fee share'
                                      : payout.type;
                              const polygonExplorerUrl = `https://polygonscan.com/tx/${payout.txHash}`;
                              return (
                                <tr key={payout.id}>
                                  <td>{new Date(payout.createtAt).toLocaleString()}</td>
                                  <td>{typeLabel}</td>
                                  <td>{payout.amount}</td>
                                  <td>
                                    <a
                                      href={polygonExplorerUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#2297D1] no-underline hover:underline"
                                    >
                                      Open Chain tx
                                    </a>
                                  </td>
                                  <td>{payout.status}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                <span className="text-sm font-semibold text-slate-900">Checkout Links ({checkoutCount})</span>
                <button
                  type="button"
                  onClick={() => toggleSection('generatedLinks')}
                  className="text-sm font-semibold text-[#2297D1] hover:underline"
                >
                  {expandedSections.generatedLinks ? 'Hide' : 'Show'}
                </button>
              </div>
              {expandedSections.generatedLinks && tx.checkoutSessions && tx.checkoutSessions.length > 0 && (
                <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  {tx.checkoutSessions.map((session, index) => {
                    const generatedLink = `${window.location.origin}/pay/${session.id}`;
                    return (
                      <div key={session.id} className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <strong className="text-[#2297D1]">Link #{index + 1}</strong>
                            <div className="text-xs text-slate-500">
                              Session ID: {session.id} | Method: {session.paymentMethod} | Status: {session.status}
                            </div>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedLink).then(() => alert('Link copied to clipboard!')).catch(() => alert('Failed to copy link'));
                            }}
                          >
                            Copy Link
                          </Button>
                        </div>
                        <div className="break-all rounded-lg border border-slate-200 bg-slate-50 p-2 font-mono text-xs">{generatedLink}</div>
                        <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
                          <span>Created: {new Date(session.createdAt).toLocaleString()}</span>
                          <span>Expires: {new Date(session.expireAt).toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {expandedSections.generatedLinks && (!tx.checkoutSessions || tx.checkoutSessions.length === 0) && (
                <p className="mb-0 mt-2 text-sm text-slate-500">No checkout links for this payment.</p>
              )}
            </div>

            <div className="flex justify-end pb-2 pt-2">
              <button
                type="button"
                onClick={onHide}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
              >
                Close
              </button>
            </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PaymentReviewModal;
